/**
 * Classify Firebase Auth sign-in failures that are transient and EXTERNAL —
 * the network dropped mid-exchange, or Google's own auth backend answered
 * 5xx — so the auth flows can warn-and-toast instead of reporting an error
 * for a failure no code change on our side can prevent (OFFICEHUB-CLIENT-52:
 * `accounts:signInWithIdp` returned 503, surfaced by the SDK as the unmapped
 * internal code `auth/error-code:-40`).
 *
 * Deliberately NOT classified as transient:
 *
 *   - `auth/internal-error`. It is the SDK's catch-all, and in this project it
 *     is also how a broken `beforeUserSignedIn` blocking function
 *     (stampOrgClaimOnSignIn — the org-claim keystone) surfaces client-side.
 *     Treating it as transient would blind Sentry to exactly the post-deploy
 *     failure it has to catch. It stays on the console.error path.
 *   - Everything user-actionable (popup-blocked, user-disabled, …) — the
 *     callers already branch on those before consulting this.
 *
 * `auth/error-code:-NN` is the SDK's spelling for a backend error code it has
 * no mapping for; these accompany 5xx-class server conditions, not client
 * bugs. If Google ever mints an unmapped code that IS actionable, the cost
 * here is a downgraded log line plus a retry toast — the sign-in failure
 * itself is still visible to the user either way.
 */
export function isTransientAuthError(error: unknown): boolean {
  const code = String((error as { code?: unknown } | null | undefined)?.code ?? '');
  return code === 'auth/network-request-failed' || code.startsWith('auth/error-code:');
}
